import { capitalize, find, isBoolean } from "lodash";
import styled from "styled-components";
import { get } from "underscore";
import { DetailsCellStyled } from "./DetailsCellStyled";
import { DetailsCellsWrapperStyled } from "./DetailsCellsWrapperStyled";
import { rearangeDisplayDate, validateSelection } from "./../../../utils/helpers/text/index";
import {
  AUDIT_SUB_EQUIPMENT_CLUSTER_TYPE,
  emptyClusterInformation,
  GXPemptyInstrumentsOptions,
  linkedInstanceList,
  SHOW_SUB_CLUSTER_KEY
} from "../../../constants";
import LOGBOOK_LABEL from "../../../utils/constants/logbookLabel";
import COMMON_LOGS_DATA_MODEL from "../../../utils/constants/commonLogsDataModel";
import DATA_MODEL_TABLE from "../../../utils/constants/dataModelTable";
import { OwcTooltip } from "@one/react";
import { ElnLinkRenderer, EN_DASH, THEME } from "@digitallab/grid-common-frontend-components";
import ApproveActionLog from "../../../features/log-book/approve-action-log/ApproveActionLog";
import RUN_LOGS_DATA_MODEL from "../../../utils/constants/runLogsDataModel";

const ChangesCellContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  font-size: 14px;
  line-height: 18px;
  font-weight: 500;
  color: #000000;
`;

const DetailCellContainer = styled.div`
  padding: 10px;
  width: 100%;
  background-color: var(--one-color-gray-50);
`;

const ClusterCellOld = styled.div`
  text-decoration: line-through;
  overflow: hidden;
  word-break: break-all;
  white-space: nowrap;
  color: var(--one-color-cobas-accent-orange-800);
`;

const ClusterCellNew = styled.div`
  overflow: hidden;
  word-break: break-all;
  white-space: nowrap;
  color: var(--one-color-accent-dark-cyan-600);
`;

const ChangesCellOld = styled.span`
  width: 100%;
  margin-right: 10px;
  text-decoration: line-through;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--one-color-cobas-accent-orange-800);
`;

const ChangesCellNew = styled.span`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--one-color-accent-dark-cyan-600);
`;

const NoValueCell = styled.span`
  width: 100%;
  margin-left: 2px;
`;

const SubEqCellStyled = styled.div`
  color: var(--one-color-gray-800);
  width: 100%;
  background-color: var(--one-color-gray-50);
  border-radius: 4px;
  padding: 20px 16px;
  box-sizing: border-box;
  margin-left: 50px;
`;

const WrapperStyled = styled.div`
  width: 100%;
  background-color: #fafafa;
  border-radius: 4px;
  border: 1px solid #d3d3d3;
`;

const NoWrapCell = ({ cellValue }) => (
  <div style={{ display: "flex" }}>
    <div
      style={{
        textDecoration: "line-through",
        color: THEME["one-color-cobas-accent-orange-800"],
        whiteSpace: "pre-wrap",
        maxWidth: "50%",
        marginRight: THEME["one-spacer-24"]
      }}
    >
      {cellValue.oldvalue}
    </div>
    <div
      style={{
        color: THEME["one-color-accent-dark-cyan-600"],
        whiteSpace: "pre-wrap",
        maxWidth: "50%"
      }}
    >
      {cellValue.newvalue}
    </div>
  </div>
);

/**
 * Prepare cell value to be displayed in detailed view of log
 *
 * @param {*} field single field of detailed view
 * @param {*} item single log row
 * @returns {*} cell to be displayed in detailed view
 */
const prepareCellBody = (field, item) => {
  let cellBody;
  if (field.component) {
    const Component = field.component;
    cellBody = <Component item={item} />;
  } else {
    cellBody = get(item, field.field) || EN_DASH;
    if (typeof cellBody === "object") {
      if (Object.keys(cellBody).length === 0) {
        cellBody = EN_DASH;
      } else {
        cellBody = cellBody.newValue?.value || cellBody.value || EN_DASH;
      }
    }
  }
  return cellBody;
};

/**
 * Separated logic for displaying Link/File list and divider
 *
 * @param {{ field: any; fields: any; index: any; CellWrapper: any; body: any; item: any; }} param0
 * @param {*} param0.field single field of detailed view
 * @param {*} param0.fields all fields of detailed view
 * @param {*} param0.index index of current field
 * @param {*} param0.CellWrapper styled component used as a wrapper for field
 * @param {*} param0.body prepared body of a field to be displayed
 * @param {*} param0.item single log row
 * @returns {*} returns both lists for externalDocument field and divider with label for Divider field
 */
const attachmentsAndDividerCells = ({ field, fields, index, CellWrapper, body, item }) => {
  if (field.lookapVal === COMMON_LOGS_DATA_MODEL.attachments.key) {
    return null;
  }

  if (field.lookapVal === COMMON_LOGS_DATA_MODEL.divider.key) {
    return <div style={{ width: "100%", fontWeight: "bold", paddingBottom: "20px" }}>{field.label}</div>;
  }

  if (field.lookapVal === COMMON_LOGS_DATA_MODEL.approveActionLog.key) {
    return <ApproveCell label="Approve Status" Wrapper={CellWrapper} item={item} />;
  }

  if (field.lookapVal === COMMON_LOGS_DATA_MODEL.externalDocument.key) {
    const attachmentField = fields.find(({ lookapVal }) => lookapVal === "attachments");

    return (
      <div style={{ width: "100%", display: "flex" }}>
        <DetailsCell
          key={`detailsCell${index}`}
          Wrapper={CellWrapper}
          label={field.label}
          {...(field.props ?? {})}
          cellValue={body}
        />
        {attachmentField && (
          <DetailsCell
            key={`detailsCell${index + 1}`}
            Wrapper={CellWrapper}
            label={attachmentField.label}
            {...(attachmentField.props ?? {})}
            cellValue={prepareCellBody(attachmentField, item)}
          />
        )}
      </div>
    );
  }
};

const SingleDetailsCell = ({ cellValue }) => {
  if (isBoolean(cellValue?.oldvalue)) {
    cellValue.oldvalue = String(cellValue?.oldvalue);
  }
  if (isBoolean(cellValue?.newvalue)) {
    cellValue.newvalue = String(cellValue?.newvalue);
  }
  return (
    <>
      <ChangesCellContainer>
        {cellValue?.oldvalue && cellValue?.oldvalue !== "null" ? (
          <>
            <ChangesCellOld id={cellValue.oldvalue}>
              {cellValue.oldvalue}
            </ChangesCellOld>
            <OwcTooltip anchor={cellValue.oldvalue}>{cellValue.oldvalue}</OwcTooltip>
          </>
        ) : (
          <NoValueCell>-</NoValueCell>
        )}
        {cellValue?.newvalue && cellValue?.newvalue !== "null" ? (
          <>
            <ChangesCellNew id={cellValue.newvalue}>
              {cellValue.newvalue}
            </ChangesCellNew>
            <OwcTooltip anchor={cellValue.newvalue}>{cellValue.newvalue}</OwcTooltip>
          </>
        ) : (
          <NoValueCell>-</NoValueCell>
        )}
      </ChangesCellContainer>
    </>
  );
};

const MultiLineDetailsCell = ({ cellValue }) => {
  return (
    <>
      {Array.isArray(cellValue)
        ? cellValue.map((value) => {
            return <SingleDetailsCell cellValue={value} />;
          })
        : ""}
    </>
  );
};

const isHeadingLabel = (label) =>
  label === LOGBOOK_LABEL.run_detail_heading || label === LOGBOOK_LABEL.action_detail_heading;

const isSubEquipmentLabel = (label) => label === COMMON_LOGS_DATA_MODEL.subEquipment.value;

const renderDetailsCellLabel = (label) => {
  if (isHeadingLabel(label) || isSubEquipmentLabel(label)) {
    return (
      <div data-testid="details-cell-label">
        <strong>{label}</strong>
      </div>
    );
  }

  return <div data-testid="details-cell-label">{label}</div>;
};

const getAuditCellValue = ({ cellValue, label, isMultiLine }) => {
  const normalizedCellValue = cellValue === "null" ? "" : cellValue;

  if (isMultiLine) {
    return <MultiLineDetailsCell cellValue={normalizedCellValue} />;
  }

  if (label === DATA_MODEL_TABLE.configurationDetails.value) {
    return <NoWrapCell cellValue={cellValue} />;
  }

  return <SingleDetailsCell cellValue={normalizedCellValue} />;
};

const getDefaultCellValue = ({ cellValue, label }) => {
  if (isHeadingLabel(label)) {
    return "";
  }

  if (isSubEquipmentLabel(label)) {
    return cellValue;
  }

  return <strong>{cellValue === "null" ? EN_DASH : cellValue}</strong>;
};

const DetailsCell = ({ label, cellValue, isAuditTrial = false, Wrapper, wrapText = false, ...props }) => {
  if (!cellValue) {
    return "";
  }

  const valueToRender = isAuditTrial
    ? getAuditCellValue({ cellValue, label, isMultiLine: props?.multiLine })
    : getDefaultCellValue({ cellValue, label });

  return (
    <>
      <Wrapper {...props} $wrapText={wrapText}>
        {renderDetailsCellLabel(label)}
        <div>{valueToRender}</div>
      </Wrapper>
    </>
  );
};

const ApproveCell = ({ label, cellValue, isAuditTrial = false, Wrapper, item, ...props }) => {
  return (
    <Wrapper {...props}>
      <div data-testid="details-cell-label">{label}</div>
      <ApproveActionLog item={item} />
    </Wrapper>
  );
};

const SubClusterCell = ({ items, relevantPositionState, prefixString = null }) => {
  let newArrItems = [];
  items.forEach((item) => {
    if (item?.type === AUDIT_SUB_EQUIPMENT_CLUSTER_TYPE.Deleted) {
      newArrItems = [item, ...newArrItems];
    } else {
      newArrItems = [...newArrItems, item];
    }
  });
  return (
    <div
      style={{ maxHeight: "200px", overflow: "auto" }}
      id="ClusterDecommission-ClusterDetailsCells-FrameWithBackground"
    >
      {newArrItems.map((item) => {
        let deleteDivCollector = "";
        let addDivCollector = "";
        let newUpdateDiv = "";
        let oldUpdateDiv = "";

        if (item.type === AUDIT_SUB_EQUIPMENT_CLUSTER_TYPE.Deleted) {
          if (relevantPositionState) {
            deleteDivCollector += `Position ${
              item.positionInCluster !== null &&
              item.positionInCluster !== "undefined" &&
              item.positionInCluster !== "null"
                ? item.positionInCluster
                : " -"
            }: `;
          }
          SHOW_SUB_CLUSTER_KEY.forEach((clusterItem, indexItem) => {
            deleteDivCollector += `${clusterItem?.label}:${
              item.oldvalue?.[clusterItem?.key] !== null &&
              item.oldvalue?.[clusterItem?.key] !== "undefined" &&
              item.oldvalue?.[clusterItem?.key] !== "null"
                ? item.oldvalue?.[clusterItem?.key]
                : " -"
            }${indexItem < SHOW_SUB_CLUSTER_KEY.length - 1 ? ", " : ""}`;
          });
        } else if (item.type === AUDIT_SUB_EQUIPMENT_CLUSTER_TYPE.Created) {
          if (relevantPositionState && item.positionInCluster) {
            addDivCollector += `Position ${
              item.positionInCluster !== null &&
              item.positionInCluster !== "undefined" &&
              item.positionInCluster !== "null"
                ? item.positionInCluster
                : " -"
            }: `;
          }
          SHOW_SUB_CLUSTER_KEY.forEach((clusterItem, indexItem) => {
            addDivCollector += `${clusterItem?.label}:${
              item.newvalue?.[clusterItem?.key] !== null &&
              item.newvalue?.[clusterItem?.key] !== "undefined" &&
              item.newvalue?.[clusterItem?.key] !== "null"
                ? item.newvalue?.[clusterItem?.key]
                : " -"
            }${indexItem < SHOW_SUB_CLUSTER_KEY.length - 1 ? ", " : ""}`;
          });
        } else if (item.type === AUDIT_SUB_EQUIPMENT_CLUSTER_TYPE.Updated) {
          if (relevantPositionState) {
            newUpdateDiv += `Position ${
              item.newvalue?.positionInCluster !== null &&
              item.newvalue?.positionInCluster !== "undefined" &&
              item.newvalue?.positionInCluster !== "null"
                ? item.newvalue?.positionInCluster
                : " "
            }: Sub-component- ${
              item.newvalue?.positionInCluster !== null &&
              item.newvalue?.positionInCluster !== "undefined" &&
              item.newvalue?.positionInCluster !== "null"
                ? item.newvalue?.positionInCluster
                : " "
            }`;
            oldUpdateDiv += `Position ${
              item.oldvalue?.positionInCluster !== null &&
              item.oldvalue?.positionInCluster !== "undefined" &&
              item.oldvalue?.positionInCluster !== "null"
                ? item.oldvalue?.positionInCluster
                : " "
            }: Sub-component- ${
              item.newvalue?.positionInCluster !== null &&
              item.newvalue?.positionInCluster !== "undefined" &&
              item.newvalue?.positionInCluster !== "null"
                ? item.newvalue?.positionInCluster
                : " "
            }`;
          }

          SHOW_SUB_CLUSTER_KEY.forEach((clusterItem, indexItem) => {
            newUpdateDiv += `${clusterItem?.label}:${
              item.newvalue?.[clusterItem?.key] !== null &&
              item.newvalue?.[clusterItem?.key] !== "undefined" &&
              item.newvalue?.[clusterItem?.key] !== "null"
                ? item.newvalue?.[clusterItem?.key]
                : " -"
            }${indexItem < SHOW_SUB_CLUSTER_KEY.length - 1 ? ", " : ""}`;

            oldUpdateDiv += `${clusterItem?.label}:${
              item?.oldvalue?.[clusterItem?.key] !== null &&
              item.oldvalue?.[clusterItem?.key] !== "undefined" &&
              item.oldvalue?.[clusterItem?.key] !== "null"
                ? item.oldvalue?.[clusterItem?.key]
                : " -"
            }${indexItem < SHOW_SUB_CLUSTER_KEY.length - 1 ? ", " : ""}`;
          });
        }
        return (
          <>
            <ClusterCellOld id={deleteDivCollector}>
              {prefixString && deleteDivCollector && `${prefixString} : `}
              {deleteDivCollector}
            </ClusterCellOld>
            <OwcTooltip anchor={deleteDivCollector}>{deleteDivCollector}</OwcTooltip>

            <ClusterCellNew id={addDivCollector}>
              {prefixString && addDivCollector && `${prefixString} : `}
              {addDivCollector}
            </ClusterCellNew>
            <OwcTooltip anchor={addDivCollector}>{addDivCollector}</OwcTooltip>

            <ClusterCellOld id={oldUpdateDiv}>
              {prefixString && oldUpdateDiv && `${prefixString} : `}
              {oldUpdateDiv}
            </ClusterCellOld>
            <OwcTooltip anchor={oldUpdateDiv}>{oldUpdateDiv}</OwcTooltip>

            <ClusterCellNew id={newUpdateDiv}>
              {prefixString && newUpdateDiv && `${prefixString} : `}
              {newUpdateDiv}
            </ClusterCellNew>
            <OwcTooltip anchor={newUpdateDiv}>{newUpdateDiv}</OwcTooltip>
          </>
        );
      })}
    </div>
  );
};

const DetailsCells = ({
  isAuditTrial = false,
  notHistory = false,
  item,
  infoMeta,
  Wrapper = DetailsCellsWrapperStyled,
  CellWrapper = DetailsCellStyled,
  relevantPositionState,
  auditAction
}) => {
  let changedKeys = Object.keys(item);
  let changeFieldConfig = infoMeta.fields;
  let displayCond = true;

  if (auditAction === "CLUSTER_UPDATE") {
    displayCond = false;
    let allFieldKeys = Object.keys(emptyClusterInformation);
    let arr = [];
    for (let key of changedKeys) {
      arr.push(allFieldKeys.includes(key));
    }
    if (arr.includes(true)) {
      displayCond = true;
    }
  }

  const getChangedValuesArray = (changedItemConfig, changes) => {
    if (
      changedItemConfig?.field === "dateOfNextMaintanance" ||
      changedItemConfig?.field === "dateOfLastMaintanance"
    ) {
      return {
        oldvalue: changes?.oldvalue ? rearangeDisplayDate(changes?.oldvalue) : null,
        newvalue: changes?.newvalue ? rearangeDisplayDate(changes?.newvalue) : null
      };
    }
    if (
      changedItemConfig?.field === "room" ||
      changedItemConfig?.field === "floor" ||
      changedItemConfig?.field === "buildingLocation" ||
      changedItemConfig?.field === "module"
    ) {
      if (changes?.value) {
        return changes?.value || null;
      } else {
        if (
          changes?.oldvalue?.value === changes?.newvalue?.value ||
          (!changes?.oldvalue?.value && !changes?.newvalue?.value)
        ) {
          return null;
        }
        return {
          oldvalue: changes?.oldvalue?.value,
          newvalue: changes?.newvalue?.value
        };
      }
    }
    if (changedItemConfig?.field === "linkedInstance") {
      const oldvalue = find(linkedInstanceList, {
        linkedInstance: changes?.oldvalue
      });
      const newvalue = find(linkedInstanceList, {
        linkedInstance: changes?.newvalue
      });
      return {
        oldvalue: oldvalue?.linkedInstanceDisplay,
        newvalue: newvalue?.linkedInstanceDisplay
      };
    }
    if (changedItemConfig?.field === "sop") {
      return getModifiedChanges(changes, "value");
    }
    if (changedItemConfig?.field === "qualificationDocuments") {
      if (changes?.value) {
        return getModifiedChanges(changes?.value, "name", "documentId");
      }
    }
    if (changedItemConfig?.field === "qualificationStatus") {
      const oldValue = validateSelection(GXPemptyInstrumentsOptions.qualificationStatus, { key: changes?.oldvalue });

      const newvalue = validateSelection(GXPemptyInstrumentsOptions.qualificationStatus, { key: changes?.newvalue });
      return {
        oldvalue: oldValue,
        newvalue: newvalue
      };
    }
    if (changedItemConfig?.field === "installedTests") {
      return getModifiedChanges(changes, "version", "name");
    }
    if (changedItemConfig?.field === DATA_MODEL_TABLE.status.key) {
      return {
        oldvalue: changes?.oldvalue ? capitalize(changes?.oldvalue) : EN_DASH,
        newvalue: changes?.newvalue ? capitalize(changes?.newvalue) : EN_DASH
      };
    }

    if (changedItemConfig?.field === "inventoryNumber") {
      return {
        oldvalue: changes?.oldvalue?.toString(),
        newvalue: changes?.newvalue?.toString()
      };
    }
    return changes;
  };

  return !isAuditTrial ? (
    <DetailCellContainer style={{ paddingLeft: notHistory ? 60 : 0 }}>
      <Wrapper data-testid="details-cells-wrapper" style={{ display: "flex", flexWrap: "wrap" }}>
        {infoMeta?.fields?.map((field, index) => {
          const body = prepareCellBody(field, item);
          const specialCells = [
            COMMON_LOGS_DATA_MODEL.attachments.key,
            COMMON_LOGS_DATA_MODEL.externalDocument.key,
            COMMON_LOGS_DATA_MODEL.divider.key,
            COMMON_LOGS_DATA_MODEL.approveActionLog.key
          ];

          if (specialCells.includes(field.lookapVal)) {
            return attachmentsAndDividerCells({ field, fields: infoMeta.fields, index, CellWrapper, body, item });
          }

          if (field.lookapVal === RUN_LOGS_DATA_MODEL.eLNid.value) {
            return (
              <CellWrapper>
                <div data-testid="details-cell-label">
                  <div data-testid="details-cell-label">{RUN_LOGS_DATA_MODEL.eLNid.value}</div>
                </div>
                <ElnLinkRenderer elnExperiment={item.eLNid} />
              </CellWrapper>
            );
          }

          return (
            <DetailsCell
              key={`detailsCell${index}`}
              Wrapper={CellWrapper}
              label={field.label}
              {...(field.props ?? {})}
              cellValue={body}
            />
          );
        })}
      </Wrapper>
    </DetailCellContainer>
  ) : (
    <WrapperStyled>
      {displayCond && (
        <Wrapper data-testid="audit-trail-details-cells-wrapper" style={{ marginLeft: "50px" }}>
          {changedKeys.map((fieldId) => {
            const changedItemConfig = find(changeFieldConfig, {
              field: fieldId
            });
            const changedItemValues = getChangedValuesArray(changedItemConfig, item[fieldId]);
            if (fieldId === DATA_MODEL_TABLE.tags.key) {
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gridColumn: "1 / -1"
                  }}
                >
                  <div
                    data-testid="details-cell-label"
                    id={DATA_MODEL_TABLE.tags.key}
                    style={{
                      fontSize: "14px",
                      lineHeight: "20px"
                    }}
                  >
                    {DATA_MODEL_TABLE.tags.value}
                  </div>
                  <div>
                    <TagLogComponent item={item[fieldId]} />
                  </div>
                </div>
              );
            }
            return (
              <>
                {changedItemConfig ? (
                  <DetailsCell
                    isAuditTrial={isAuditTrial}
                    Wrapper={CellWrapper}
                    label={changedItemConfig?.label}
                    {...(changedItemConfig?.props ?? {})}
                    cellValue={changedItemValues}
                  ></DetailsCell>
                ) : (
                  ""
                )}
              </>
            );
          })}
        </Wrapper>
      )}
      <SubEqCellStyled>
        {item.hasOwnProperty("subEquipment") && item?.subEquipment.length > 0 && (
          <SubClusterCell items={item?.subEquipment} relevantPositionState={relevantPositionState} />
        )}
        {item.hasOwnProperty("leadingClusterSoftware") && Object.keys(item?.leadingClusterSoftware).length > 0 && (
          <SubClusterCell
            items={[item?.leadingClusterSoftware]}
            relevantPositionState={relevantPositionState}
            prefixString={"Leading cluster software"}
          />
        )}
      </SubEqCellStyled>
    </WrapperStyled>
  );
};

export default DetailsCells;

const concatChanges = (changes, primaryKey, secondaryKey) => {
  if (changes) {
    const primaryValue = changes[primaryKey] ? `${changes[primaryKey]}` : "";
    const secondaryValue = changes[secondaryKey] ? `, ${changes[secondaryKey]}` : "";
    return `${primaryValue}${secondaryValue}`;
  } else {
    return "";
  }
};

const getModifiedChanges = (changes, primaryKey, secondaryKey) => {
  return changes.map((item) => {
    const itemWithPrimaryKey = item[primaryKey];
    if (itemWithPrimaryKey) {
      if (primaryKey === "version") {
        return {
          oldvalue: `${itemWithPrimaryKey[secondaryKey]}, ${itemWithPrimaryKey?.oldvalue}`,
          newvalue: `${itemWithPrimaryKey[secondaryKey]}, ${itemWithPrimaryKey?.newvalue}`
        };
      } else {
        return {
          oldvalue: `${itemWithPrimaryKey?.oldvalue}, ${itemWithPrimaryKey[secondaryKey]}`,
          newvalue: `${itemWithPrimaryKey?.newvalue}, ${itemWithPrimaryKey[secondaryKey]}`
        };
      }
    }
    if (primaryKey === "version") {
      return {
        oldvalue: concatChanges(item?.oldvalue, secondaryKey, primaryKey),
        newvalue: concatChanges(item?.newvalue, secondaryKey, primaryKey)
      };
    } else {
      return {
        oldvalue: concatChanges(item?.oldvalue, primaryKey, secondaryKey),
        newvalue: concatChanges(item?.newvalue, primaryKey, secondaryKey)
      };
    }
  });
};

const TagLogComponent = ({ item = [] }) => {
  const html = item.map((x) =>
    x?.type === "Created" ? (
      <span>{x?.newvalue}</span>
    ) : (
      x?.oldvalue &&
      x?.oldvalue !== "null" && (
        <>
          <span style={{ textDecoration: "line-through" }} id={x?.oldvalue}>
            {x?.oldvalue}
          </span>
        </>
      )
    )
  );
  const tagsData = html.map((x, index) => (index === 0 ? x : <>,{x}</>));
  return (
    <ChangesCellContainer>
      <ChangesCellNew>{tagsData}</ChangesCellNew>
    </ChangesCellContainer>
  );
};
